import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { TextField, Typography, Container, Paper, Box, Alert } from '@mui/material';
import { forgotPasswordRequest } from '../remote/authRequest';
import Logo from '../components/LayoutComponents/Logo';
import PurpleButton from '../components/LayoutComponents/PurpleButton';
import Background from '../components/LayoutComponents/Background';
import theme from '../components/LayoutComponents/theme';
import { reCaptcha } from '../utils/reCaptcha';
import LoadingCircle from '../components/LayoutComponents/LoadingCircle';



function ForgotPasswordPage({ isMobile }) {
    const { register, handleSubmit, formState: { errors }, setError, } = useForm();
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [captchaValue, setCaptchaValue] = useState('');
    const [loading, setLoading] = useState(false);

    const validateForm = () => {
        if (!captchaValue) {
            setError('captcha', { type: 'manual', message: 'No Captcha token.' });
            return false;
        }
        return true;
    };

    const onSubmit = async (data, event) => {
        event.preventDefault();
        if (!validateForm()) return;

        setLoading(true);

        try {
            const response = await forgotPasswordRequest(data.email, captchaValue);
            if (response.status === 200) {
                setSuccessMessage('You will receive a password reset link shortly.');
                setErrorMessage('');
            }
        } catch (error) {
            console.error('Error during password reset request:', error);
            const errorMessage = error?.response?.data || "An error occurred. Please try again.";
            setErrorMessage(errorMessage);
            setSuccessMessage('');
        } finally {
            setLoading(false);
            refreshCaptcha();
        }
    };

    const refreshCaptcha = () => {
        reCaptcha(
            "forgotPassword",
            (token) => setCaptchaValue(token)
        );
    };

    // Call refreshCaptcha whenever you want a new token
    useEffect(() => {
        if (!captchaValue)
            refreshCaptcha();
    }, [captchaValue]);
    return (
        <Background>
            <Container
                component="main"
                maxWidth="xs"
                sx={{
                    minHeight: "50vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: isMobile ? "center" : "flex-start",
                    pt: isMobile ? 2 : 8,
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        width: "100%",
                        maxWidth: 380,
                        p: { xs: 2, sm: 3 },
                        boxSizing: "border-box",
                    }}
                >
                    <Logo />
                    <Box textAlign="center" mb={2}>
                        <Typography variant='h5' sx={{ color: theme.colors.purple }}>
                            Forgot Password
                        </Typography>
                        <Typography variant="subtitle2" color='#6a336a'>
                            Enter your email address to reset your password.
                        </Typography>
                    </Box>
                    {successMessage && <Alert severity="success">{successMessage}</Alert>}
                    {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
                    {/* Loader overlay */}
                    {loading && (
                        <Box
                            sx={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                width: '100vw',
                                height: '100vh',
                                bgcolor: 'rgba(255,255,255,0.6)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 2000,
                            }}
                        >
                            <LoadingCircle />
                        </Box>
                    )}
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Box mt={2}>
                            <TextField
                                fullWidth
                                label="Email Address"
                                name="email"
                                variant="outlined"
                                {...register('email', {
                                    required: 'Email is required',
                                    pattern: {
                                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                        message: 'Invalid email address'
                                    }
                                })}
                                error={!!errors.email}
                                helperText={errors.email ? errors.email.message : ''}
                                size={isMobile ? "small" : "medium"}
                            />
                        </Box>
                        <Box mt={2} display="flex" justifyContent="center">
                            <PurpleButton type="submit" label='Send Reset Link' fullWidth={isMobile} />
                        </Box>
                    </form>
                </Paper>
            </Container>
        </Background>
    );
};

export default ForgotPasswordPage;
